import { useCallback, KeyboardEvent } from 'react';
import { JSONContent } from '@tiptap/core';

import {
  ARROW_LEFT,
  ARROW_RIGHT,
  ENTER,
  ESCAPE,
  SPACEBAR,
  TAB,
} from 'approot/shared/ally/keyboard.hooks';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import {
  DragId,
  CANCELLED,
  MOVE_LEFT,
  MOVE_RIGHT,
  PICKUP,
  PLACED,
  KeyboardInteraction,
} from './interactive-drag-drop.constants';
import { DropdownKebab } from 'approot/shared/dropdown/dropdown-kebab';
import * as Styled from './interactive-drag.style';
import { ImageViewWrapper } from 'approot/activity-builder/components/image/image.style';
import { ComponentImageContainer } from 'approot/activity-builder/components/component-image.style';
import { InquisitiveActivitiesResponseVariant } from 'global/constants';
import {
  isTextImageVariant,
  isTextVariant,
} from '../../interactive-response/shared/interactive-response-shared.utils';
import { getRichtextValue } from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import { INTERACTIVE_RICHTEXT_EXTENSIONS } from '../../interactive-response/interactive-response-shared.constants';
import { useInteractiveAudioContext } from '../../interactive-response/shared/interactive-audio.context';
import { useInteractiveDrag } from './interactive-drag.hooks';
import InteractiveResponseAudioButton from '../../interactive-response/shared/interactive-response-audio-button';

type InteractiveDragCommonProps = {
  id: DragId;
  description: string;
  imageUrl?: string | null;
  isFit?: boolean;
  baseColour: string | null;
  variant?: InquisitiveActivitiesResponseVariant;
  text: string | null;
  richtext: JSONContent | null;
  audioUrl: string | null;
  isDisabled?: boolean;
  isCorrect?: boolean;
  droppedAt?: string;
  overAt?: string;
  scaleTo?: number;
  keyboard: {
    target?: DragId;
    onKeyboardInteraction: (payload: KeyboardInteraction) => void;
  };
  scale?: number;
  onExpandImage?: () => void;
};

export const InteractiveDrag = ({
  id,
  imageUrl,
  isFit,
  description: imageDescription,
  isDisabled,
  isCorrect,
  droppedAt,
  overAt,
  keyboard,
  baseColour,
  variant,
  text,
  richtext,
  audioUrl,
  onExpandImage,
  scale,
  scaleTo,
}: InteractiveDragCommonProps) => {
  const { playAudio, pauseAudio, isPlaying, currentAudioId } =
    useInteractiveAudioContext();
  const isAudioPlaying = isPlaying && currentAudioId === id;
  const { description, drag, isDragging, ref, kebabOptions } =
    useInteractiveDrag({
      imageDescription,
      variant,
      richtext,
      text,
      id,
      isDisabled,
      scale,
      keyboard,
      audioUrl,
      onExpandImage,
      isAudioPlaying,
    });

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key;
      // 'this item' isn't very descriptive but it's better than 'null'
      const itemDescription = description || 'this item';

      if (isDisabled && key !== TAB) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (key === ESCAPE && keyboard.target) {
        e.preventDefault();
        e.stopPropagation();
        keyboard.onKeyboardInteraction({
          id,
          action: CANCELLED,
          announce: `You have cancelled moving ${itemDescription}`,
        });
        return;
      }

      if (key === TAB && keyboard.target) {
        // when keyboard dragging tabing is locked down
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (key === ARROW_RIGHT || key === ARROW_LEFT) {
        e.preventDefault();
        e.stopPropagation();
        if (keyboard.target) {
          keyboard.onKeyboardInteraction({
            id,
            action: key === ARROW_RIGHT ? MOVE_RIGHT : MOVE_LEFT,
            announce: `You have moved ${itemDescription} to drop area`,
          });
        }

        return;
      }

      if (key === ENTER) {
        e.preventDefault();
        e.stopPropagation();
        onExpandImage?.();
      }

      if (key === SPACEBAR) {
        e.preventDefault();
        e.stopPropagation();

        // end keyboard drag mode
        if (keyboard.target) {
          keyboard.onKeyboardInteraction({
            id,
            action: PLACED,
            announce: `You have placed ${itemDescription} at drop area`,
          });
        } else {
          keyboard.onKeyboardInteraction({
            id,
            action: PICKUP,
            announce: `You have lifted ${itemDescription}`,
          });
        }
      }
    },
    [id, keyboard, description, isDisabled, onExpandImage]
  );

  const imageContent = isTextVariant(variant) ? null : isFit && imageUrl ? (
    <ImageViewWrapper>
      <ComponentImageContainer $imageHasLoaded>
        <img src={imageUrl} alt="" />
      </ComponentImageContainer>
    </ImageViewWrapper>
  ) : imageUrl ? (
    <Styled.InteractiveItemImage
      $imageUrl={imageUrl}
      $imageHasLoaded
      role="image"
    />
  ) : null;

  const showAudioButtonOnly = !onExpandImage && audioUrl;

  return (
    <Styled.InteractiveDragContainer ref={ref} $scaleTo={scaleTo}>
      <Styled.InteractiveDragItem
        id={`interactive-drag-${id}`}
        ref={node => {
          drag(node);
        }}
        $isDragging={isDragging}
        $isKeyboardDragging={id === keyboard.target}
        $isDisabled={!!isDisabled}
        $baseColour={baseColour}
        tabIndex={0}
        role="button"
        onKeyDown={handleKeyPress}
        style={
          scale && isDragging ? { transform: `scale(${scale})` } : undefined
        }
      >
        {isTextVariant(variant) ? (
          <Styled.InteractiveText
            $baseColour={baseColour}
            dangerouslySetInnerHTML={{
              __html: getRichtextValue(
                richtext,
                INTERACTIVE_RICHTEXT_EXTENSIONS
              ),
            }}
          />
        ) : isTextImageVariant(variant) ? (
          <>
            {imageContent}
            {text && (
              <Styled.InteractiveTextImageContainer aria-hidden>
                {text}
              </Styled.InteractiveTextImageContainer>
            )}
          </>
        ) : (
          imageContent
        )}

        <ScreenReaderOnly>
          {description}
          {overAt && ` item over area: ${overAt}`}
          {droppedAt && ` dropped at area: ${droppedAt}`}
          {isCorrect !== undefined
            ? isCorrect
              ? ' correct'
              : ' incorrect'
            : ''}
          {isDisabled ? ' disabled' : ''}
        </ScreenReaderOnly>

        {kebabOptions.length > 0 && !isDragging && !showAudioButtonOnly && (
          <Styled.InteractiveDragItemButtonContainer>
            <DropdownKebab
              aria-label={`Select to display options for ${description}`}
              menuAlignRight
              $isSmallToggle
              options={kebabOptions}
              closeMenuOnSelect={false}
              onSelect={option => {
                if (option.value === 'audio') {
                  if (isAudioPlaying) {
                    pauseAudio();
                  } else {
                    playAudio(id);
                  }
                } else if (option.value === 'expand') {
                  onExpandImage?.();
                }
              }}
            />
          </Styled.InteractiveDragItemButtonContainer>
        )}

        {showAudioButtonOnly && !isDragging && (
          <Styled.InteractiveDragItemButtonContainer>
            <InteractiveResponseAudioButton
              itemId={id}
              description={description}
            />
          </Styled.InteractiveDragItemButtonContainer>
        )}
      </Styled.InteractiveDragItem>
    </Styled.InteractiveDragContainer>
  );
};

export const InteractiveClickOrDrag = ({
  id,
  imageUrl,
  isFit,
  description: imageDescription,
  isDisabled,
  isCorrect,
  droppedAt,
  overAt,
  keyboard,
  baseColour,
  variant,
  text,
  richtext,
  audioUrl,
  scale,
  scaleTo,
  isDropZoneActive = false,
  onClick,
  onExpandImage,
}: InteractiveDragCommonProps & {
  isDropZoneActive?: boolean;
  onClick?: () => void;
}) => {
  const { playAudio, pauseAudio, isPlaying, currentAudioId } =
    useInteractiveAudioContext();
  const isAudioPlaying = isPlaying && currentAudioId === id;
  const { description, drag, isDragging, ref, kebabOptions } =
    useInteractiveDrag({
      imageDescription,
      variant,
      richtext,
      text,
      id,
      isDisabled,
      scale,
      keyboard,
      audioUrl,
      onExpandImage,
      isAudioPlaying,
    });

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key;
      const itemDescription = description || 'this item';

      if (isDisabled && key !== TAB) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (key === ESCAPE && keyboard.target) {
        e.preventDefault();
        e.stopPropagation();
        keyboard.onKeyboardInteraction({
          id,
          action: CANCELLED,
          announce: `You have cancelled moving ${itemDescription}`,
        });
        return;
      }

      if (key === TAB && keyboard.target) {
        // when keyboard dragging tabing is locked down
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (key === ARROW_RIGHT || key === ARROW_LEFT) {
        e.preventDefault();
        e.stopPropagation();
        if (keyboard.target) {
          keyboard.onKeyboardInteraction({
            id,
            action: key === ARROW_RIGHT ? MOVE_RIGHT : MOVE_LEFT,
            announce: `You have moved ${itemDescription} to drop area`,
          });
        }

        return;
      }

      if (key === SPACEBAR || key === ENTER) {
        e.preventDefault();
        e.stopPropagation();

        // end keyboard drag mode
        if (keyboard.target) {
          keyboard.onKeyboardInteraction({
            id,
            action: PLACED,
            announce: `You have placed ${itemDescription} at drop area`,
          });
        } else {
          keyboard.onKeyboardInteraction({
            id,
            action: PICKUP,
            announce: `You have lifted ${itemDescription}`,
          });
        }
      }
    },
    [id, keyboard, description, isDisabled]
  );

  const imageContent = isTextVariant(variant) ? null : isFit && imageUrl ? (
    <ImageViewWrapper>
      <ComponentImageContainer $imageHasLoaded>
        <img src={imageUrl} alt="" />
      </ComponentImageContainer>
    </ImageViewWrapper>
  ) : imageUrl ? (
    <Styled.InteractiveItemImage
      $imageUrl={imageUrl}
      $imageHasLoaded
      role="image"
    />
  ) : null;

  const showAudioButtonOnly = !onExpandImage && audioUrl;

  return (
    <Styled.InteractiveDragContainer ref={ref} $scaleTo={scaleTo}>
      {isDropZoneActive ? (
        <Styled.InteractiveClickItem
          role="button"
          $isDisabled={!!isDisabled}
          $baseColour={baseColour}
          data-drop-zone-clickable="true"
          onClick={event => {
            // prevent a click on this drag item from bubbling up to the parent drop zone
            event.preventDefault();
            event.stopPropagation();
            onClick?.();
          }}
        >
          {isTextVariant(variant) ? (
            <Styled.InteractiveText
              $baseColour={baseColour}
              dangerouslySetInnerHTML={{
                __html: getRichtextValue(
                  richtext,
                  INTERACTIVE_RICHTEXT_EXTENSIONS
                ),
              }}
            />
          ) : isTextImageVariant(variant) ? (
            <>
              {imageContent}
              {text && (
                <Styled.InteractiveTextImageContainer aria-hidden>
                  {text}
                </Styled.InteractiveTextImageContainer>
              )}
            </>
          ) : (
            imageContent
          )}

          <ScreenReaderOnly>
            {description}
            Click or press enter to place in the selected drop zone
            {isCorrect !== undefined
              ? isCorrect
                ? ' correct'
                : ' incorrect'
              : ''}
            {isDisabled ? ' disabled' : ''}
          </ScreenReaderOnly>

          {kebabOptions.length > 0 && !showAudioButtonOnly && (
            <Styled.InteractiveDragItemButtonContainer>
              <DropdownKebab
                aria-label={`Select to display options for ${description}`}
                menuAlignRight
                $isSmallToggle
                options={kebabOptions}
                closeMenuOnSelect={false}
                onSelect={option => {
                  if (option.value === 'audio') {
                    if (isAudioPlaying) {
                      pauseAudio();
                    } else {
                      playAudio(id);
                    }
                  } else if (option.value === 'expand') {
                    onExpandImage?.();
                  }
                }}
                onToggle={(_, event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              />
            </Styled.InteractiveDragItemButtonContainer>
          )}

          {showAudioButtonOnly && !isDragging && (
            <Styled.InteractiveDragItemButtonContainer>
              <InteractiveResponseAudioButton
                itemId={id}
                description={description}
              />
            </Styled.InteractiveDragItemButtonContainer>
          )}
        </Styled.InteractiveClickItem>
      ) : (
        <Styled.InteractiveDragItem
          id={`interactive-drag-${id}`}
          ref={node => {
            drag(node);
          }}
          $isDragging={isDragging}
          $isKeyboardDragging={id === keyboard.target}
          $isDisabled={!!isDisabled}
          $baseColour={baseColour}
          tabIndex={0}
          role="button"
          onKeyDown={handleKeyPress}
          style={
            scale && isDragging ? { transform: `scale(${scale})` } : undefined
          }
          onClick={event => {
            // prevent a click on this drag item from bubbling up to the parent drop zone
            event.preventDefault();
            event.stopPropagation();
            onClick?.();
          }}
          data-drop-zone-clickable="true"
        >
          {isTextVariant(variant) ? (
            <Styled.InteractiveText
              $baseColour={baseColour}
              dangerouslySetInnerHTML={{
                __html: getRichtextValue(
                  richtext,
                  INTERACTIVE_RICHTEXT_EXTENSIONS
                ),
              }}
            />
          ) : isTextImageVariant(variant) ? (
            <>
              {imageContent}
              {text && (
                <Styled.InteractiveTextImageContainer aria-hidden>
                  {text}
                </Styled.InteractiveTextImageContainer>
              )}
            </>
          ) : (
            imageContent
          )}

          <ScreenReaderOnly>
            {description}
            {overAt && ` item over area: ${overAt}`}
            {droppedAt && ` dropped at area: ${droppedAt}`}
            {isCorrect !== undefined
              ? isCorrect
                ? ' correct'
                : ' incorrect'
              : ''}
            {isDisabled ? ' disabled' : ''}
          </ScreenReaderOnly>

          {kebabOptions.length > 0 && !showAudioButtonOnly && (
            <Styled.InteractiveDragItemButtonContainer>
              <DropdownKebab
                aria-label={`Actions for ${description}`}
                menuAlignRight
                $isSmallToggle
                options={kebabOptions}
                closeMenuOnSelect={false}
                onSelect={option => {
                  if (option.value === 'audio') {
                    if (isAudioPlaying) {
                      pauseAudio();
                    } else {
                      playAudio(id);
                    }
                  } else if (option.value === 'expand') {
                    onExpandImage?.();
                  }
                }}
                onToggle={(_, event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              />
            </Styled.InteractiveDragItemButtonContainer>
          )}

          {showAudioButtonOnly && !isDragging && (
            <Styled.InteractiveDragItemButtonContainer>
              <InteractiveResponseAudioButton
                itemId={id}
                description={description}
              />
            </Styled.InteractiveDragItemButtonContainer>
          )}
        </Styled.InteractiveDragItem>
      )}
    </Styled.InteractiveDragContainer>
  );
};
